import { createClient } from '@/lib/supabase/client';
import { useUserStore } from '@/store/userStore';
import { trackEvent, setUserProperties } from '@/lib/mixpanel-client';

/**
 * Interface representing a user's credit usage information
 */
interface CreditUsage {
  credits_used_today: number;
  credits_used_this_month: number;
  last_reset_date: string;
  purchased_credits: number;
  one_time_credits_granted: boolean;
  one_time_credits_used: number;
}

/**
 * Get the monthly credit allowance based on subscription tier
 * - Free tier: 0 monthly credits (only 50 one-time welcome credits)
 * - Premium tier: 500 monthly credits
 * - Lifetime tier: 1500 monthly credits
 */
export async function getMonthlyCreditAllowance(tier: string): Promise<number> {
  switch (tier) {
    case 'lifetime':
      return 1500;
    case 'premium':
      return 500;
    case 'free':
    default:
      return 0; // Free tier only gets one-time welcome credits
  }
}

/**
 * Retrieves the current credit usage for a user
 * - Checks if monthly credits need to be reset (new month)
 * - For new users, initializes with default values
 * - For existing users, returns current usage
 */
export async function getCurrentCredits(userId: string): Promise<CreditUsage> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('profiles')
    .select('credits_used_today, credits_used_this_month, last_reset_date, purchased_credits, one_time_credits_granted, one_time_credits_used')
    .eq('id', userId)
    .single();

  if (error) throw error;

  // If no credit data exists, return defaults for a new user
  if (!data) {
    return {
      credits_used_today: 0,
      credits_used_this_month: 0,
      last_reset_date: new Date().toISOString(),
      purchased_credits: 0,
      one_time_credits_granted: false, // New users will get 50 welcome credits on first credit use
      one_time_credits_used: 0,
    };
  }

  // Check if we need to reset monthly credits (beginning of a new month)
  const lastReset = new Date(data.last_reset_date);
  const now = new Date();
  const needsMonthlyReset = 
    lastReset.getMonth() !== now.getMonth() || 
    lastReset.getFullYear() !== now.getFullYear();

  if (needsMonthlyReset) {
    // Reset monthly credits at the beginning of a new month
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        credits_used_this_month: 0,
        last_reset_date: now.toISOString(),
      })
      .eq('id', userId);

    if (updateError) throw updateError;

    return {
      credits_used_today: data.credits_used_today || 0,
      credits_used_this_month: 0, // Reset for new month
      last_reset_date: now.toISOString(),
      purchased_credits: data.purchased_credits || 0,
      one_time_credits_granted: data.one_time_credits_granted || false,
      one_time_credits_used: data.one_time_credits_used || 0,
    };
  }

  return {
    credits_used_today: data.credits_used_today || 0,
    credits_used_this_month: data.credits_used_this_month || 0,
    last_reset_date: data.last_reset_date,
    purchased_credits: data.purchased_credits || 0,
    one_time_credits_granted: data.one_time_credits_granted || false,
    one_time_credits_used: data.one_time_credits_used || 0,
  };
}

/**
 * Consumes credits for a user's action
 * - Credit consumption order:
 *   1. Monthly credits (for Premium/Lifetime tiers)
 *   2. One-time welcome credits (for free tier, 50 credits total)
 *   3. Purchased credits (if monthly and one-time are exhausted)
 * 
 * @param userId The user ID to consume credits for
 * @param amount The number of credits to consume (defaults to 1)
 * @param reason The reason for consuming credits (for tracking/analytics)
 * @returns Boolean indicating success or failure
 */
export async function consumeCredits(
  userId: string, 
  amount: number = 1, 
  reason: string = "Menu Processing"
): Promise<boolean> {
  const supabase = createClient();
  
  console.log(`[Credits] Attempting to consume ${amount} credit(s) for user ${userId}`);
  
  // Get current subscription tier
  const { data: subscriptionData, error: subscriptionError } = await supabase
    .from('subscriptions')
    .select('tier, price_id')
    .eq('user_id', userId)
    .single();

  if (subscriptionError) throw subscriptionError;

  // Detect lifetime plan by price_id, otherwise use tier
  const tier = subscriptionData.price_id === "price_1QfEOKKrqyeof4bLJRxXk7vu" 
    ? "lifetime" 
    : subscriptionData.tier;

  // Get monthly allowance based on tier
  const monthlyAllowance = await getMonthlyCreditAllowance(tier);

  // Get current credit usage
  const currentCredits = await getCurrentCredits(userId);

  // Handle credit consumption based on tier
  let result: boolean;
  let creditSource: string = "Unknown";
  
  if (tier === 'free') {
    result = await consumeFreeCredits(userId, amount, currentCredits);
    if (result) {
      const oneTimeCreditsAvailable = currentCredits.one_time_credits_granted 
        ? Math.max(0, 50 - currentCredits.one_time_credits_used) 
        : 0;
      
      creditSource = oneTimeCreditsAvailable >= amount 
        ? "One-Time Welcome" 
        : "Purchased Credits";
    }
  } else {
    result = await consumePaidCredits(userId, amount, currentCredits, monthlyAllowance, tier);
    if (result) {
      const remainingMonthly = Math.max(0, monthlyAllowance - currentCredits.credits_used_this_month);
      creditSource = remainingMonthly >= amount 
        ? "Monthly Allowance" 
        : "Purchased Credits";
    }
  }
  
  // Track credit consumption in Mixpanel if successful
  if (result) {
    // Get latest credit totals after consumption
    const updatedCredits = await getCurrentCredits(userId);
    const totalRemainingCredits = tier === 'free'
      ? (currentCredits.one_time_credits_granted ? Math.max(0, 50 - updatedCredits.one_time_credits_used) : 0) + updatedCredits.purchased_credits
      : Math.max(0, monthlyAllowance - updatedCredits.credits_used_this_month) + updatedCredits.purchased_credits;
    
    // Track the event
    trackEvent('Credits Consumed', {
      'Amount': amount,
      'Reason': reason,
      'User ID': userId,
      'Tier': tier,
      'Credit Source': creditSource
    });
    
    // Update total credits remaining property
    setUserProperties({
      'Total Credits Remaining': totalRemainingCredits
    });
  }
  
  return result;
}

/**
 * Consumes credits for free tier users
 * - Free users get 50 one-time welcome credits
 * - Once those are used, they must use purchased credits
 */
async function consumeFreeCredits(
  userId: string, 
  amount: number, 
  currentCredits: CreditUsage
): Promise<boolean> {
  const supabase = createClient();
  console.log(`[Credits] Processing free tier credit consumption`);

  // For free users, check one-time welcome credits first
  let remainingAmount = amount;
  let updatedOneTimeCreditsUsed = currentCredits.one_time_credits_used;
  let updatedPurchasedCredits = currentCredits.purchased_credits;
  
  // Check if one-time credits are granted and available
  const oneTimeCreditsAvailable = currentCredits.one_time_credits_granted 
    ? Math.max(0, 50 - currentCredits.one_time_credits_used) 
    : 0;
  
  // If this is a new user without one-time credits, grant them their welcome 50 credits
  let oneTimeCreditsGranted = currentCredits.one_time_credits_granted;
  if (!oneTimeCreditsGranted && currentCredits.one_time_credits_used === 0 && currentCredits.purchased_credits === 0) {
    oneTimeCreditsGranted = true; 
    console.log(`[Credits] Granting 50 one-time welcome credits to new user`);
  }
  
  // Use welcome credits first if available
  if (oneTimeCreditsGranted && oneTimeCreditsAvailable > 0) {
    const oneTimeToUse = Math.min(oneTimeCreditsAvailable, remainingAmount);
    updatedOneTimeCreditsUsed += oneTimeToUse;
    remainingAmount -= oneTimeToUse;
    console.log(`[Credits] Using ${oneTimeToUse} one-time welcome credit(s)`);
  }
  
  // Then use purchased credits if needed
  if (remainingAmount > 0) {
    if (currentCredits.purchased_credits >= remainingAmount) {
      updatedPurchasedCredits -= remainingAmount;
      console.log(`[Credits] Using ${remainingAmount} purchased credit(s)`);
      remainingAmount = 0;
    } else {
      console.log(`[Credits] Insufficient credits. Needed: ${remainingAmount}, Available purchased: ${currentCredits.purchased_credits}`);
      return false;
    }
  }
  
  // If we've made it here, we can afford the credits - update the database
  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      one_time_credits_granted: oneTimeCreditsGranted,
      one_time_credits_used: updatedOneTimeCreditsUsed,
      purchased_credits: updatedPurchasedCredits,
    })
    .eq('id', userId);

  if (updateError) throw updateError;
  
  // Update the local store
  updateUserStore(userId);
  
  console.log(`[Credits] Successfully consumed ${amount} credit(s) for free tier. New status:
    - One-time credits used: ${updatedOneTimeCreditsUsed}/50
    - Purchased credits remaining: ${updatedPurchasedCredits}
  `);
  
  return true;
}

/**
 * Consumes credits for premium and lifetime users
 * - Premium users get 500 monthly credits
 * - Lifetime users get 1500 monthly credits
 * - Credits refresh at the beginning of each month
 * - After monthly credits are used, purchased credits are consumed
 */
async function consumePaidCredits(
  userId: string, 
  amount: number, 
  currentCredits: CreditUsage,
  monthlyAllowance: number,
  tier: string
): Promise<boolean> {
  const supabase = createClient();
  console.log(`[Credits] Processing ${tier} tier credit consumption`);
  
  // Calculate remaining monthly credits
  const remainingMonthly = Math.max(0, monthlyAllowance - currentCredits.credits_used_this_month);
  
  // Check if we have enough credits total (monthly + purchased)
  if (remainingMonthly + currentCredits.purchased_credits < amount) {
    console.log(`[Credits] Insufficient credits. 
      - Monthly remaining: ${remainingMonthly}
      - Purchased: ${currentCredits.purchased_credits}
      - Needed: ${amount}`);
    return false;
  }
  
  // Use monthly credits first, then purchased
  let remainingAmount = amount;
  let updatedMonthlyCredits = currentCredits.credits_used_this_month;
  let updatedPurchasedCredits = currentCredits.purchased_credits;
  
  if (remainingMonthly > 0) {
    const monthlyToUse = Math.min(remainingMonthly, remainingAmount);
    updatedMonthlyCredits += monthlyToUse;
    remainingAmount -= monthlyToUse;
    console.log(`[Credits] Using ${monthlyToUse} monthly credit(s)`);
  }
  
  // Then use purchased credits if needed
  if (remainingAmount > 0) {
    updatedPurchasedCredits -= remainingAmount;
    console.log(`[Credits] Using ${remainingAmount} purchased credit(s)`);
  }
  
  // Update credit usage in database
  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      credits_used_this_month: updatedMonthlyCredits,
      purchased_credits: updatedPurchasedCredits,
    })
    .eq('id', userId);

  if (updateError) throw updateError;
  
  // Update the local store
  updateUserStore(userId);
  
  console.log(`[Credits] Successfully consumed ${amount} credit(s) for ${tier} tier. New status:
    - Monthly credits used: ${updatedMonthlyCredits}/${monthlyAllowance}
    - Purchased credits remaining: ${updatedPurchasedCredits}
  `);
  
  return true;
}

/**
 * Helper function to update the userStore after credit consumption
 * This keeps the UI in sync with the latest credit usage
 */
async function updateUserStore(userId: string): Promise<void> {
  // Get the current profile data to update the store
  const supabase = createClient();
  const { data: updatedProfile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (updatedProfile) {
    // Update the UserStore with the new profile data
    useUserStore.getState().setProfile({
      ...updatedProfile,
      email: useUserStore.getState().profile?.email || '',
    });
  }
}

/**
 * Adds purchased credits to a user's account
 * Purchased credits never expire and are used after monthly credits
 */
export async function addPurchasedCredits(userId: string, amount: number): Promise<void> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('profiles')
    .select('purchased_credits')
    .eq('id', userId)
    .single();

  if (error) throw error;

  const currentPurchasedCredits = data?.purchased_credits || 0;
  const newPurchasedCredits = currentPurchasedCredits + amount;

  const { error: updateError } = await supabase
    .from('profiles')
    .update({
      purchased_credits: newPurchasedCredits,
    })
    .eq('id', userId);

  if (updateError) throw updateError;
  
  // Update the UserStore
  updateUserStore(userId);
} 